import { usePrevious } from '@sequencehq/utils'
import {
  Button,
  GreyWhite,
  Modal,
  Center,
  Flex
} from '@sequencehq/core-components'
import { isEqual } from '@sequencehq/utils/dates'
import Spinner from 'components/Loading'
import { Provider, useAtomValue, useSetAtom } from 'jotai'
import { FC, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { usePermissions } from 'lib/rbac'
import { ExecuteQuoteReviewStep } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/steps/ExecuteQuoteReviewStep'
import { ExecuteQuoteSelectStep } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/steps/ExecuteQuoteSelectStep'
import { useEligibleSchedulesForCustomer } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/hooks/useEligibleSchedulesForCustomer'
import type { QuoteExecuteScheduleAction } from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import {
  hasApplicableScheduleAtom,
  hydrateRenewalDataAtom,
  isExistingSelectionBlockedAtom,
  isRenewalLoadingAtom,
  newScheduleStartDateAtom,
  renewalModeAtom,
  renewalStartDateAtom,
  resetBillingPeriodAtom,
  scheduleActionAtom,
  updateRenewalFormAtom,
  validScheduleCountAtom
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/executeQuote.atoms'

type ExecuteRenewalQuoteModalProps = {
  onClose: () => void
  onConfirm: (result: {
    startDate: Date | undefined
    scheduleAction: QuoteExecuteScheduleAction
  }) => void
  submitting: boolean
}

type Step = 'select' | 'review'

/**
 * The syncing mechanism for getting Cube data into the modal's jotai atoms, so that
 * we have a nice set of data to work with, and not too much of a link to Cube's context
 * (which could make refactoring a pain later).
 */
const useCubeDataFromContext = () => {
  const cubeContext = useCubeContext()

  const quoteData = cubeContext.queries.rawData.data
  const configuration = cubeContext.queries.rawData.configuration
  const customerId = quoteData.common.customerId
  const currency = cubeContext.queries.selectedCurrency
  const totalPricingDuration = cubeContext.queries.totalPricingDuration
  const quoteStartDate = quoteData.common.startDate

  const hydrateData = useSetAtom(hydrateRenewalDataAtom)
  useEffect(() => {
    hydrateData({
      context: {
        customerId,
        currency,
        totalPricingDuration,
        quoteData,
        configuration
      }
    })
  }, [
    customerId,
    currency,
    totalPricingDuration,
    quoteData,
    configuration,
    hydrateData
  ])

  return {
    customerId,
    currency,
    quoteStartDate
  }
}

/**
 * Hydrates the modal's jotai atoms from the cube and the loaded eligible
 * schedules, drives the selection defaults, and exposes the execute handlers.
 * Colocated with the modal because it owns the modal's shared state.
 */
const useExecuteRenewalQuoteModal = (props: ExecuteRenewalQuoteModalProps) => {
  const { customerId, currency, quoteStartDate } = useCubeDataFromContext()

  // Fetches the eligible schedules and writes them (with the loading flag and a
  // preselection) straight into the renewal atoms.
  useEligibleSchedulesForCustomer({ customerId, currency })

  const { logEvent } = useLogEvent()
  const { roles } = usePermissions()

  const updateForm = useSetAtom(updateRenewalFormAtom)

  // Seed the new-schedule start date from the quote's own start date (which we
  // already sync in from the cube). The renewal date is deliberately not seeded
  // here: it defaults from the selected schedule (the day after it ends) when a
  // schedule is (pre)selected, and seeding it too would race that default. The
  // cube can hand us a fresh Date instance for the same instant on each render,
  // so only re-seed when the date actually changes by value — otherwise we'd
  // overwrite the user's edits every render.
  const previousQuoteStartDate = usePrevious(quoteStartDate)
  useEffect(() => {
    const startDateUnchanged =
      previousQuoteStartDate &&
      quoteStartDate &&
      isEqual(previousQuoteStartDate, quoteStartDate)

    if (startDateUnchanged) {
      return
    }
    updateForm({ newScheduleStartDate: quoteStartDate })
  }, [quoteStartDate, previousQuoteStartDate, updateForm])

  const isRenewalLoading = useAtomValue(isRenewalLoadingAtom)
  const hasApplicableSchedule = useAtomValue(hasApplicableScheduleAtom)
  const mode = useAtomValue(renewalModeAtom)

  // Once the schedules have finished loading, default to "add to existing" if
  // one can take the renewal. Applied exactly once (after the load completes),
  // so the user's subsequent choice is never overridden.
  const appliedDefaultModeRef = useRef(false)
  useEffect(() => {
    if (isRenewalLoading || appliedDefaultModeRef.current) {
      return
    }
    appliedDefaultModeRef.current = true
    if (hasApplicableSchedule) {
      updateForm({ mode: 'existing' })
    }
  }, [isRenewalLoading, hasApplicableSchedule, updateForm])

  const renewalStartDate = useAtomValue(renewalStartDateAtom)
  const newScheduleStartDate = useAtomValue(newScheduleStartDateAtom)
  const scheduleAction = useAtomValue(scheduleActionAtom)
  const resetBillingPeriod = useAtomValue(resetBillingPeriodAtom)
  const validScheduleCount = useAtomValue(validScheduleCountAtom)
  const isExistingSelectionBlocked = useAtomValue(
    isExistingSelectionBlockedAtom
  )

  const applyingToExisting = mode === 'existing'

  // Instrument the execute here rather than passing analytics up to the caller.
  const logExecuteEvent = () => {
    logEvent({
      eventName: 'quote_executed_with_renewal_option',
      metadata: {
        optionTaken: match(scheduleAction?.mode)
          .with('ADD_TO_EXISTING', () => 'add_to_existing')
          .with('REPLACE_EXISTING', () => 'replace_existing')
          .otherwise(() => 'new_schedule'),
        validScheduleCount,
        billingPeriodReset: applyingToExisting ? resetBillingPeriod : false,
        userRole: roles.map(role => role.roleName).join(', ') || 'none'
      }
    })
  }

  const executeNewSchedule = () => {
    if (newScheduleStartDate && scheduleAction) {
      logExecuteEvent()
      props.onConfirm({
        startDate: newScheduleStartDate,
        scheduleAction
      })
    }
  }

  const executeExistingSchedule = () => {
    if (scheduleAction?.mode === 'ADD_TO_EXISTING') {
      logExecuteEvent()
      // The renewal date anchors the appended phases; the backend truncates the
      // existing schedule there when it falls before the schedule's end.
      props.onConfirm({
        startDate: renewalStartDate,
        scheduleAction
      })
    }
  }

  return {
    isRenewalLoading,
    applyingToExisting,
    isExistingSelectionBlocked,
    executeNewScheduleDisabled: !newScheduleStartDate,
    executeNewSchedule,
    executeExistingSchedule
  }
}

/**
 * The two-step renewal execute experience (choose how to execute → review →
 * execute), shown for renewal quotes when the feature is enabled. New-business
 * quotes (and any quote with the feature disabled) use the create-schedule
 * dialog. State is shared across the steps and footer via modal-scoped jotai
 * atoms, so the modal is wrapped in its own jotai Provider.
 */
export const ExecuteRenewalQuoteModal: FC<
  ExecuteRenewalQuoteModalProps
> = props => (
  <Provider>
    <ExecuteRenewalQuoteModalContent {...props} />
  </Provider>
)

const ExecuteRenewalQuoteModalContent: FC<
  ExecuteRenewalQuoteModalProps
> = props => {
  const [step, setStep] = useState<Step>('select')
  const {
    isRenewalLoading,
    applyingToExisting,
    isExistingSelectionBlocked,
    executeNewScheduleDisabled,
    executeNewSchedule,
    executeExistingSchedule
  } = useExecuteRenewalQuoteModal(props)

  return (
    <Modal isOpen onClose={props.onClose} data-testid="modal.executeQuote">
      <Modal.Header showCloseButton withBorder onClose={props.onClose}>
        Execute quote
      </Modal.Header>
      <Modal.Content>
        {isRenewalLoading ? (
          <Center height="120px">
            <Spinner />
          </Center>
        ) : (
          match(step)
            .with('select', () => <ExecuteQuoteSelectStep />)
            .with('review', () => <ExecuteQuoteReviewStep />)
            .exhaustive()
        )}
      </Modal.Content>
      <Modal.Footer withBorder>
        <Flex justifyContent="flex-end" width="100%" gap="8px">
          {match({ step, applyingToExisting, isRenewalLoading })
            .with({ isRenewalLoading: true }, () => (
              <>
                <Button variant="secondary" onClick={props.onClose}>
                  Cancel
                </Button>
                <Button variant="primary" disabled>
                  Continue
                </Button>
              </>
            ))
            .with({ step: 'review' }, () => (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setStep('select')}
                  data-testid="executeQuoteModal.back"
                >
                  Back
                </Button>
                <Button
                  data-testid="executeQuoteModal.submit"
                  variant="primary"
                  disabled={props.submitting}
                  onClick={() => !props.submitting && executeExistingSchedule()}
                >
                  <Flex gap="8px" alignItems="center">
                    {props.submitting && (
                      <Spinner height="16px" width="16px" color={GreyWhite} />
                    )}
                    Execute quote
                  </Flex>
                </Button>
              </>
            ))
            .with({ step: 'select', applyingToExisting: true }, () => (
              <>
                <Button variant="secondary" onClick={props.onClose}>
                  Cancel
                </Button>
                <Button
                  data-testid="executeQuoteModal.continue"
                  variant="primary"
                  disabled={isExistingSelectionBlocked || props.submitting}
                  onClick={() =>
                    !(isExistingSelectionBlocked || props.submitting) &&
                    setStep('review')
                  }
                >
                  Continue
                </Button>
              </>
            ))
            .otherwise(() => (
              <>
                <Button variant="secondary" onClick={props.onClose}>
                  Cancel
                </Button>
                <Button
                  data-testid="executeQuoteModal.submit"
                  variant="primary"
                  disabled={executeNewScheduleDisabled || props.submitting}
                  onClick={() =>
                    !(executeNewScheduleDisabled || props.submitting) &&
                    executeNewSchedule()
                  }
                >
                  <Flex gap="8px" alignItems="center">
                    {props.submitting && (
                      <Spinner height="16px" width="16px" color={GreyWhite} />
                    )}
                    Execute quote
                  </Flex>
                </Button>
              </>
            ))}
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}
